import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppStore } from '@store/appStore';
import { useAuthStore } from '@store/authStore';
import useQuoteForm from '../hooks/useQuoteForm';
import useUserData from '../hooks/useUserData';
import useQuoteProcessor from '../hooks/useQuoteProcessor';
import { mapAircraftToPayload } from '../utils/transformBidData';
import ClientInformationSection from '../components/generate/ClientInformationSection';
import FlightDetailsSection from '../components/generate/FlightDetailsSection';
import AircraftOptionsSection from '../components/generate/AircraftOptionsSection';
import QuoteSettingsSection from '../components/generate/QuoteSettingsSection';
import QuotePreview from '../components/generate/QuotePreview';
import QuoteCustomizeModal from '../components/generate/QuoteCustomizeModal';

/**
 * GenerateQuote Page
 * Main page for generating and sending quote quotes
 */
const GenerateQuote = () => {
  const [searchParams] = useSearchParams();
  const isQuickQuote = searchParams.has('quickquote');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [customizeModalOpen, setCustomizeModalOpen] = useState(false);
  const { shortlistAircraft, selectedTripGroup, clearShortlist, clearSelectedTripGroup } = useAppStore();
  const { user } = useAuthStore();

  const { data: userData } = useUserData(user?.id);

  const {
    formData,
    updateClient,
    updateFlights,
    updateSettings,
    updateBidOptions,
    updateBidOptionProperty,
    updateBidOptionProperties,
    toggleSection,
    goToNextSection,
    goToPreviousSection,
    expandedSections,
    unlockedSections,
    completedSections,
    resetForm,
    getFormErrors
  } = useQuoteForm();

  const {
    handleSaveDraft,
    handleSendQuote,
    isSending,
    isSavingDraft,
    error,
    setError,
  } = useQuoteProcessor(userData);

  const isDataLoading = !shortlistAircraft && !selectedTripGroup;

  // Memoize allBidOptions to prevent unnecessary recalculations
  const allBidOptions = React.useMemo(() => {
    if (shortlistAircraft && shortlistAircraft.length > 0) {
      return shortlistAircraft.map(mapAircraftToPayload);
    }
    return selectedTripGroup?.bids?.flatMap(bid => bid.bid_options || []) || [];
  }, [shortlistAircraft, selectedTripGroup]);

  // Reset form when component mounts or navigation changes
  useEffect(() => {
    if (isQuickQuote) {
      clearShortlist();
      clearSelectedTripGroup();
      resetForm();
    } else {
      resetForm({ preserveBidOptions: !!selectedTripGroup });
    }
  }, [searchParams]);

  // Initialize and update bidOptions when shortlistAircraft changes
  useEffect(() => {
    if (allBidOptions.length > 0 && !isDataLoading) {
      updateBidOptions(allBidOptions);
    }
  }, [allBidOptions, isDataLoading, updateBidOptions]);

  // Open preview (validates form first)
  const handleOpenPreview = () => {
    const errors = getFormErrors();
    const firstError = Object.values(errors)[0];
    if (firstError) {
      setError(firstError || 'Please complete all sections before previewing');
      setTimeout(() => setError(null), 5000);
      return;
    }
    setPreviewOpen(true);
  };

  // Handle customize modal confirm - update settings and send quote
  const handleCustomizeConfirm = (customizedData) => {
    const updatedSettings = {
      ...formData.settings,
      ...customizedData,
    };
    updateSettings(updatedSettings);
    setCustomizeModalOpen(false);
    setPreviewOpen(true);

    const quoteData = {
      ...formData,
      settings: updatedSettings,
      flights: formData.flights.flights,
      flightType: formData.flights.type,
    };
    handleSendQuote(quoteData);
  };

  // Wrap handlers to also close preview
  const onSaveDraft = async (quoteData) => {
    await handleSaveDraft(quoteData);
    setPreviewOpen(false);
  };


  return (
    <div className="generate-quote-page pb-24">
      <main className="max-w-4xl px-1 py-3">
        {selectedTripGroup?.tripNumber &&
          <div className="text-right mb-4">
            <h4 className="text-gray-500 inline bg-white rounded-lg px-3 py-1">Trip: <span className="text-primary-500 font-semibold">{selectedTripGroup.tripNumber}</span></h4>
          </div>}
        <div className="space-y-4">
          {error && (
            <div
              role="alert"
              className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg"
            >
              <p className="text-sm font-medium">{error}</p>
            </div>
          )}

          <ClientInformationSection
            data={formData.client}
            onUpdate={updateClient}
            isComplete={completedSections.client}
            isExpanded={expandedSections.client}
            onToggle={() => toggleSection('client')}
            onNext={() => goToNextSection('client')}
          />

          <FlightDetailsSection
            data={formData.flights}
            onUpdate={updateFlights}
            isComplete={completedSections.flights}
            isExpanded={expandedSections.flights}
            onToggle={() => toggleSection('flights')}
            onNext={() => goToNextSection('flights')}
            onBack={() => goToPreviousSection('flights')}
            isClientComplete={completedSections.client}
            isUnlocked={unlockedSections.flights}
          />

          <AircraftOptionsSection
            formData={formData}
            isComplete={completedSections.aircraft}
            isExpanded={expandedSections.aircraft}
            onToggle={() => toggleSection('aircraft')}
            onNext={() => goToNextSection('aircraft')}
            onBack={() => goToPreviousSection('aircraft')}
            isUnlocked={unlockedSections.aircraft}
            onUpdate={updateBidOptionProperty}
            onBatchUpdate={updateBidOptionProperties}
            isDataLoading={isDataLoading}
          />

          <QuoteSettingsSection
            settings={formData.settings}
            onUpdate={updateSettings}
            isComplete={completedSections.settings}
            isExpanded={expandedSections.settings}
            onToggle={() => toggleSection('settings')}
            onPreview={handleOpenPreview}
            onBack={() => goToPreviousSection('settings')}
            isAircraftComplete={completedSections.aircraft}
            isUnlocked={unlockedSections.settings}
            flightOptions={formData.bidOptions}
          />
        </div>
      </main>

      <QuoteCustomizeModal
        isOpen={customizeModalOpen}
        onClose={() => setCustomizeModalOpen(false)}
        settings={formData.settings}
        clientData={formData.client?.clientData}
        onConfirm={handleCustomizeConfirm}
        bidOptions={formData.bidOptions}
      />

      <QuotePreview
        isOpen={previewOpen}
        onClose={() => !isSending && !isSavingDraft && setPreviewOpen(false)}
        quoteData={{
          ...formData,
          flights: formData.flights.flights,
          flightType: formData.flights.type
        }}
        onSend={() => {  console.log("formData", formData); setPreviewOpen(false); setCustomizeModalOpen(true); }}
        onSaveDraft={onSaveDraft}
        isSending={isSending}
        isSavingDraft={isSavingDraft}
      />
    </div>
  );
};

export default GenerateQuote;
